import { useCallback, useEffect, useState } from "react";
import api from "../api";
import { pushSupported, pushEnabled, enablePush, diagnose, isIOS, isStandalone } from "../push";

export default function PushSettings() {
  const [enabled, setEnabled] = useState(pushEnabled());
  const [busy, setBusy] = useState("");
  const [devs, setDevs] = useState(null);
  const [checks, setChecks] = useState([]);
  const [esiti, setEsiti] = useState(null);
  const denied = pushSupported() && Notification.permission === "denied";
  const iosNotInstalled = isIOS && !isStandalone;

  const loadDevs = () => api.get("/push/status").then((r) => setDevs(r.data)).catch(() => {});
  const runDiag = useCallback(() => { diagnose().then(setChecks).catch(() => {}); }, []);
  useEffect(() => { loadDevs(); runDiag(); }, [runDiag]);

  const activate = async () => {
    setBusy("on");
    try { await enablePush(); setEnabled(true); loadDevs(); runDiag(); alert("✅ Notifiche attivate su questo dispositivo!"); }
    catch (e) { runDiag(); alert(e.message); }
    finally { setBusy(""); }
  };
  const sendTest = async (viaEvento = false) => {
    setBusy(viaEvento ? "evento" : "test");
    setEsiti(null);
    try {
      const r = await api.post(`/push/test${viaEvento ? "?via_evento=true" : ""}`);
      setEsiti(r.data.esiti || []);
      loadDevs();
    } catch (e) { setEsiti([{ tipo: "—", esito: "errore", dettaglio: e?.response?.data?.detail || e.message }]); }
    finally { setBusy(""); }
  };

  return (
    <div className="panel" data-testid="push-settings-panel">
      <h3>🔔 Notifiche sul telefono</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Le notifiche (🚨 messaggi urgenti, 💳 pagamenti, 📅 prenotazioni, ✉️ email dai clienti) compaiono sulla
        <strong> schermata di blocco dell'iPhone</strong>, anche ad app chiusa.
      </p>

      <div style={{ border: "1px solid rgba(148,163,184,.25)", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }} data-testid="push-diagnosis">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
          <strong style={{ fontSize: 13.5 }}>🩺 Autodiagnosi di questo dispositivo</strong>
          <button className="btn small" onClick={runDiag} data-testid="push-diag-refresh">Rifai il controllo</button>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13, lineHeight: 1.9 }}>
          {checks.map((c) => (
            <li key={c.nome} data-testid={`push-check-${c.nome.replace(/\s+/g, "-").toLowerCase()}`}>
              <span style={{ color: c.ok ? "#22a35a" : "#f87171" }}>{c.ok ? "✅" : "❌"}</span> {c.nome}
              {c.nota && <span className="muted" style={{ fontSize: 12 }}> — {c.nota}</span>}
            </li>
          ))}
        </ul>
      </div>

      {devs && (
        <div style={{ fontSize: 13, marginBottom: 8 }} data-testid="push-devices">
          Dispositivi registrati: <strong>{devs.devices}</strong>
          {" — "}iPhone/iPad: <strong style={{ color: devs.iphone ? "#22a35a" : "#f87171" }}>{devs.iphone}</strong>
          {" · "}altri (computer/Android): <strong>{devs.altri}</strong>
          {devs.iphone === 0 && <span style={{ color: "#f87171" }}> ← il tuo iPhone NON è ancora registrato</span>}
          {(devs.elenco || []).some((d) => d.ultimo_errore) && (
            <ul style={{ fontSize: 12, marginTop: 6 }}>
              {(devs.elenco || []).filter((d) => d.ultimo_errore).map((d, i) => (
                <li key={i} style={{ color: "#f87171" }}>{d.tipo}: {d.ultimo_errore}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {iosNotInstalled && (
        <div style={{ background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.4)", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 10 }} data-testid="push-ios-warning">
          ⚠️ <strong>Stai usando Safari:</strong> su iPhone le notifiche funzionano solo dall'app installata.
          Segui i passaggi qui sotto (2 minuti, una volta sola).
        </div>
      )}

      <div style={{ fontSize: 13, marginBottom: 10 }} data-testid="push-status">
        Stato su questo dispositivo:{" "}
        {!pushSupported()
          ? <span style={{ color: "#f59e0b" }}>⚠️ non supportate qui — su iPhone apri l'app <strong>dalla schermata Home</strong> (vedi sotto)</span>
          : denied
          ? <span style={{ color: "#f87171" }}>🚫 bloccate — riattivale da Impostazioni iPhone → Notifiche → CRM</span>
          : enabled
          ? <span style={{ color: "#22a35a" }}>✅ attive</span>
          : <span style={{ color: "#f59e0b" }}>⭕ da attivare</span>}
      </div>
      <div className="toolbar" style={{ gap: 8 }}>
        {!denied && (
          <button className="btn primary" onClick={activate} disabled={busy === "on"} data-testid="push-activate-btn">
            {busy === "on" ? "Attivazione…" : enabled ? "🔁 Ripeti l'attivazione su questo dispositivo" : "🔔 Attiva le notifiche su questo dispositivo"}
          </button>
        )}
        <button className="btn" onClick={() => sendTest(false)} disabled={busy === "test"} data-testid="push-test-btn">
          {busy === "test" ? "Invio…" : "📲 Invia notifica di prova"}
        </button>
        <button className="btn" onClick={() => sendTest(true)} disabled={busy === "evento"} data-testid="push-test-event-btn">
          {busy === "evento" ? "Invio…" : "📨 Prova un avviso vero (come un'email in arrivo)"}
        </button>
      </div>

      {esiti && (
        <div style={{ marginTop: 10, fontSize: 13 }} data-testid="push-test-result">
          {esiti.length === 0 ? <span className="muted">Nessun dispositivo registrato.</span> : esiti.map((e, i) => (
            <div key={i} style={{ color: e.esito === "ok" ? "#22a35a" : "#f87171" }}>
              {e.esito === "ok" ? "✅" : "❌"} {e.tipo}: {e.esito === "ok" ? "notifica consegnata al servizio di Apple/Google — deve comparire sul telefono in pochi secondi" : e.dettaglio}
            </div>
          ))}
        </div>
      )}

      <details style={{ marginTop: 12 }} open={iosNotInstalled}>
        <summary style={{ cursor: "pointer", fontSize: 13 }} className="muted">📱 Come attivarle sull'iPhone (una volta sola)</summary>
        <ol style={{ fontSize: 13, lineHeight: 1.8, marginTop: 8 }}>
          <li>Apri il CRM in <strong>Safari</strong> → tocca <strong>Condividi</strong> (quadrato con freccia) → <strong>«Aggiungi alla schermata Home»</strong></li>
          <li>Apri l'app <strong>dall'icona sulla Home</strong> (non da Safari)</li>
          <li>Torna qui in Impostazioni → tocca <strong>«Attiva le notifiche»</strong> → consenti quando l'iPhone lo chiede</li>
          <li>Tocca <strong>«Invia notifica di prova»</strong> e blocca il telefono: la notifica deve comparire sulla schermata di blocco 🎉</li>
          <li>Fatto: qui sopra «iPhone/iPad» passerà da 0 a 1 ✓</li>
        </ol>
      </details>
    </div>
  );
}
